import { allAreasInConfig, allLocationsInConfig } from '../../widgetChartHelpers';
import { getTranslationKeyByOption, periods } from '../../widgetHelpers';
import { timestampToFormattedDateString } from '../../../common/helpers';
import { WIDGET_TYPES, USER_TYPES } from '../../../../constants';

/**
 * Returns data for showing widget configuration
 * @returns {{ label: string, value: string }[]}
 */
export function configurationData() {
    /**
     * Returns item with list of selected assignment areas
     * @returns {{ label: string, value: string }}
     */
    function getAssignmentAreas() {
        let value = allAreasInConfig(this.allAssignmentAreas, this.configuration.aa)
            ? this.$t('reporting.commonLabels.allAA')
            : this.configuration.aa.join(', ');

        return { label: 'reporting.commonLabels.aAreas', value };
    }

    /**
     * Returns item with list of selected locations or string like "All locations" if every
     * location from assignment area(s) is in the selection list
     * @returns {{ label: string, value: string }}
     */
    function getLocations() {
        let value = allLocationsInConfig(
            this.allAssignmentAreas,
            this.configuration.locations,
            this.configuration.aa
        )
            ? this.$t('reporting.commonLabels.allLocations')
            : this.configuration.locations.join(', ');

        return { label: 'reporting.commonLabels.locations', value };
    }

    /**
     * Returns item with translated name of selected period
     * @returns {{ label: string, value: string }}
     */
    function getPeriod() {
        let now = new Date();

        let nowTimestamp = now.getTime();
        let lastTimestamp = new Date().setDate(now.getDate() - this.configuration.period + 1);

        let nowDate = timestampToFormattedDateString(nowTimestamp, 'dd MMM'); // for example, 08 Oct
        let lastDate = timestampToFormattedDateString(lastTimestamp, 'dd MMM'); // for example, 01 Oct

        let dateString = lastDate !== nowDate ? `${lastDate} - ${nowDate}` : nowDate;

        let translationValue = this.$t(getTranslationKeyByOption(periods, this.configuration.period));

        return {
            label: 'reporting.commonLabels.period',
            value: `${translationValue} (${dateString})`,
        };
    }

    /**
     * Returns item with list of names for all specified call types or 'All call types'
     * @returns {{ label: string, value: string }}
     */
    function getCallTypeNames() {
        let value =
            this.configuration.call_types.length === this.callTypes.length
                ? this.$t('reporting.commonLabels.allCallTypes')
                : this.callTypes
                      .filter(ct => this.configuration.call_types.includes(ct.id))
                      .map(ct => ct.name)
                      .join(', ');

        return { label: 'reporting.commonLabels.callTypes', value };
    }

    /**
     * Returns item with list of selected assignment areas except renamed and inexistent
     * @returns {{ label: string, value: string, class: string }}
     */
    function getTrueAssinmentAreas(assignmentAreas) {
        let assignmentAreasClass = '';
        if (assignmentAreas.value === this.$t('reporting.commonLabels.allAA')) {
            return { ...assignmentAreas };
        }
        let allAssignmentAreasNames = this.allAssignmentAreas.map(area => area.name);
        let assignmentAreaTrueNames = assignmentAreas.value
            .split(', ')
            .map(areaName => {
                if (!allAssignmentAreasNames.includes(areaName)) {
                    assignmentAreasClass = 'td-warning';
                    this.setNeedsAttention(true);
                    return areaName + this.$t('common.doesNotExistMark');
                }
                return areaName;
            })
            .join(', ');

        return {
            ...assignmentAreas,
            value: assignmentAreaTrueNames,
            class: assignmentAreasClass,
        };
    }

    /**
     * Returns item with list of selected locations except renamed or inexistent,
     * or string like "All locations" if every location from assignment area(s)
     * is in the selection list
     * @returns {{ label: string, value: string, class: string }}
     */
    function getTrueLocations(locations) {
        let locationsClass = '';
        let allLocationsNames = this.allAssignmentAreas
            .reduce((result, area) => result.concat(area.locations), [])
            .map(location => location.name);

        if (locations.value === this.$t('reporting.commonLabels.allLocations')) {
            return { ...locations };
        }
        let locationsTrueNames = locations.value
            .split(', ')
            .map(locationName => {
                if (!allLocationsNames.includes(locationName)) {
                    locationsClass = 'td-warning';
                    this.setNeedsAttention(true);
                    return locationName + this.$t('common.doesNotExistMark');
                }
                return locationName;
            })
            .join(', ');
        return { ...locations, value: locationsTrueNames, class: locationsClass };
    }

    const data = [];
    this.setNeedsAttention(false);
    switch (this.widgetType) {
        case WIDGET_TYPES.SystemActivity:
        case WIDGET_TYPES.TargetTime: {
            let assignmentAreas = getAssignmentAreas.call(this);
            let locations = getLocations.call(this);
            let period = getPeriod.call(this);
            if (this.userType === USER_TYPES.SYS_ADMIN) {
                let trueAssignmentAreas = getTrueAssinmentAreas.call(this, assignmentAreas);
                data.push(trueAssignmentAreas);
            }
            let trueLocations = getTrueLocations.call(this, locations);

            return data.concat([trueLocations, period]);
        }
        case WIDGET_TYPES.StaffPerformance: {
            let assignmentAreas = getAssignmentAreas.call(this);
            let period = getPeriod.call(this);
            let callTypeNames = getCallTypeNames.call(this);
            if (this.userType === USER_TYPES.SYS_ADMIN) {
                let trueAssignmentAreas = getTrueAssinmentAreas.call(this, assignmentAreas);
                data.push(trueAssignmentAreas);
            }
            return data.concat([callTypeNames, period]);
        }
        case WIDGET_TYPES.NursePresence: {
            let assignmentAreas = getAssignmentAreas.call(this);
            let locations = getLocations.call(this);
            let period = getPeriod.call(this);
            if (this.userType === USER_TYPES.SYS_ADMIN) {
                let trueAssignmentAreas = getTrueAssinmentAreas.call(this, assignmentAreas);
                data.push(trueAssignmentAreas);
            }
            let trueLocations = getTrueLocations.call(this, locations);
            return data.concat([
                trueLocations,
                period,
                { label: 'reporting.commonLabels.callType', value: this.nurseCall },
            ]);
        }
        case WIDGET_TYPES.RecentEvents: {
            let assignmentAreas = getAssignmentAreas.call(this);
            let locations = getLocations.call(this);
            let callTypeNames = getCallTypeNames.call(this);
            if (this.userType === USER_TYPES.SYS_ADMIN) {
                let trueAssignmentAreas = getTrueAssinmentAreas.call(this, assignmentAreas);
                data.push(trueAssignmentAreas);
            }
            let trueLocations = getTrueLocations.call(this, locations);
            return data.concat([
                trueLocations,
                callTypeNames,
                { label: 'dashboard.labels.duration', value: this.duration },
            ]);
        }
        case WIDGET_TYPES.ExceptionTime: {
            let assignmentAreas = getAssignmentAreas.call(this);
            let locations = getLocations.call(this);
            if (this.userType === USER_TYPES.SYS_ADMIN) {
                let trueAssignmentAreas = getTrueAssinmentAreas.call(this, assignmentAreas);
                data.push(trueAssignmentAreas);
            }
            let trueLocations = getTrueLocations.call(this, locations);
            return data.concat([trueLocations]);
        }
        case WIDGET_TYPES.AverageTime: {
            let assignmentAreas = getAssignmentAreas.call(this);
            let locations = getLocations.call(this);
            if (this.userType === USER_TYPES.SYS_ADMIN) {
                let trueAssignmentAreas = getTrueAssinmentAreas.call(this, assignmentAreas);
                data.push(trueAssignmentAreas);
            }
            let trueLocations = getTrueLocations.call(this, locations);
            return data.concat([
                trueLocations,
                { label: 'reporting.commonLabels.callType', value: this.callType },
                { label: 'dashboard.labels.callProcessingType', value: this.processingTime },
            ]);
        }
    }
}
